/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowUp, ExternalLink, Mail, MapPin, Building2, FileWarning } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

/* ─────────────────── DATA ─────────────────── */
const SECTIONS = [
  { id: 'principle',      num: '1',  title: 'General Principle: All Sales Final' },
  { id: 'cancellation',   num: '2',  title: 'Refund in the Event of Cancellation' },
  { id: 'rescheduling',   num: '3',  title: 'Rescheduling' },
  { id: 'non-refundable', num: '4',  title: 'Non-Refundable Circumstances' },
  { id: 'sponsorships',   num: '5',  title: 'Sponsorships' },
  { id: 'transfers',      num: '6',  title: 'Transfers' },
  { id: 'chargebacks',    num: '7',  title: 'Chargebacks & Payment Disputes' },
  { id: 'force-majeure',  num: '8',  title: 'Force Majeure' },
  { id: 'statutory',      num: '9',  title: 'Statutory Rights' },
  { id: 'amendments',     num: '10', title: 'Amendments' },
  { id: 'contact',        num: '11', title: 'Contact' },
];

const EFFECTIVE_DATE = 'September 4, 2026';

/* ─────────────────── COMPONENTS ─────────────────── */
const SectionAnchor = ({ id, num, title, children }) => (
  <section id={id} data-testid={`refund-section-${id}`} className="scroll-mt-28 pb-10">
    <div className="flex items-baseline gap-3 mb-4 md:mb-5">
      <span className="text-xs md:text-sm font-mono font-bold px-2 py-0.5 rounded bg-[var(--accent-bg)] border border-[var(--accent-primary)]/40 text-[var(--accent-primary)]">
        {num}
      </span>
      <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-white leading-tight">{title}</h2>
    </div>
    <div className="space-y-3 text-sm md:text-[15px] text-[var(--text-secondary)] leading-relaxed">
      {children}
    </div>
  </section>
);

const P = ({ num, children }) => (
  <p className="pl-0 md:pl-14 relative">
    {num && (
      <span className="inline md:absolute md:left-0 md:top-0 font-mono text-[13px] font-semibold text-white/60 pr-2 md:pr-0">
        {num}
      </span>
    )}
    <span>{children}</span>
  </p>
);

const SubList = ({ items }) => (
  <ol className="pl-6 md:pl-20 mt-2 space-y-2 list-none">
    {items.map((it, i) => (
      <li key={i} className="flex gap-2">
        <span className="font-mono text-[12px] font-semibold text-white/50 flex-shrink-0 w-6">{it.label}</span>
        <span>{it.text}</span>
      </li>
    ))}
  </ol>
);

const SubHeading = ({ children }) => (
  <div className="pt-2 pb-1 md:ml-14">
    <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-[var(--accent-primary)]">
      <span className="w-6 h-px bg-[var(--accent-primary)]" />
      {children}
    </div>
  </div>
);

/* ─────────────────── PAGE ─────────────────── */
const LayerSeekRefundPolicyPage = () => {
  const [activeId, setActiveId] = useState(SECTIONS[0].id);
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    document.title = 'Refund & Cancellation Policy · LayerSeek';
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: '-30% 0px -60% 0px', threshold: 0 }
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });

    const onScroll = () => setShowTop(window.scrollY > 600);
    window.addEventListener('scroll', onScroll);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]" data-testid="refund-policy-page">
      <Header />

      {/* Hero */}
      <section className="relative pt-24 sm:pt-28 pb-10 sm:pb-14 border-b border-[var(--border-subtle)] overflow-hidden">
        <div
          className="absolute -top-32 -right-24 w-96 h-96 rounded-full blur-3xl opacity-20 pointer-events-none"
          style={{ background: 'var(--accent-primary)' }}
        />
        <div className="container mx-auto px-4 sm:px-6 relative">
          <Link
            to="/layerseek"
            data-testid="refund-back-link"
            className="inline-flex items-center gap-2 text-xs sm:text-sm text-[var(--text-muted)] hover:text-[var(--accent-primary)] transition-colors mb-6"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to LayerSeek
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--accent-bg)] border border-[var(--accent-primary)]/40 text-[10px] sm:text-xs font-semibold text-[var(--accent-primary)] uppercase tracking-widest mb-4">
              Legal · LayerSeek
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-3">
              Refund &amp; Cancellation Policy
            </h1>
            <p className="text-sm sm:text-base text-[var(--text-secondary)] max-w-3xl mb-4">
              How Zedit Limited (t/a LayerSeek) handles refunds, credits, transfers and cancellations for
              tickets, sponsorships, and other paid entitlements.
            </p>
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-subtle)] text-[var(--text-muted)]">
                Effective Date · <span className="text-white font-semibold">{EFFECTIVE_DATE}</span>
              </span>
              <Link
                to="/layerseek/terms"
                data-testid="refund-tos-link"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--accent-primary)] transition-colors"
              >
                Terms of Service
                <ExternalLink className="w-3 h-3" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Body */}
      <div className="container mx-auto px-4 sm:px-6 py-10 md:py-14">
        <div className="grid lg:grid-cols-[240px_1fr] xl:grid-cols-[280px_1fr] gap-8 lg:gap-12">
          {/* Sticky TOC */}
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div
              className="rounded-2xl border border-white/8 p-4 sm:p-5"
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
              }}
            >
              <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--accent-primary)] mb-3">
                On this page
              </div>
              <nav data-testid="refund-toc" className="space-y-0.5 max-h-[65vh] overflow-y-auto pr-1">
                {SECTIONS.map((s) => {
                  const active = s.id === activeId;
                  return (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      data-testid={`refund-toc-${s.id}`}
                      className={`block px-2.5 py-1.5 rounded-md text-xs sm:text-[13px] transition-colors border-l-2 ${
                        active
                          ? 'bg-[var(--accent-bg)] border-[var(--accent-primary)] text-white font-semibold'
                          : 'border-transparent text-[var(--text-muted)] hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <span className="font-mono text-[10px] text-white/40 mr-1.5">{s.num}</span>
                      {s.title}
                    </a>
                  );
                })}
              </nav>
            </div>

            {/* Side contact */}
            <div
              className="mt-5 rounded-2xl border border-white/8 p-4 hidden lg:block"
              style={{
                background: 'linear-gradient(135deg, rgba(218,255,1,0.05) 0%, rgba(255,255,255,0.01) 100%)',
              }}
            >
              <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--accent-primary)] mb-2">
                Refund or transfer request?
              </div>
              <a
                href="mailto:hi@zedit.io"
                data-testid="refund-side-contact-email"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-white hover:text-[var(--accent-primary)] transition-colors"
              >
                <Mail className="w-3.5 h-3.5" />
                hi@zedit.io
              </a>
            </div>
          </aside>

          {/* Content */}
          <div className="min-w-0">
            {/* Intro */}
            <section className="pb-10 border-b border-[var(--border-subtle)] mb-4">
              <div
                className="rounded-2xl border border-white/8 p-5 sm:p-6"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                }}
              >
                <p className="text-sm md:text-[15px] text-[var(--text-secondary)] leading-relaxed">
                  This Refund and Cancellation Policy (the <strong className="text-white">"Refund Policy"</strong>) sets
                  out the terms on which <strong className="text-white">Zedit Limited</strong>, a company incorporated
                  in the Ras Al Khaimah Digital Assets Oasis, United Arab Emirates, with its registered office at Office A,
                  RAK DAO Business Centre, ROC Office, Ground Floor, Al Rifaa, Sheikh Mohammed Bin Zayed Road, Ras Al
                  Khaimah, United Arab Emirates (hereinafter{' '}
                  <strong className="text-white">"Zedit," "we," "us"</strong>), operating under the trade name{' '}
                  <strong className="text-white">"LayerSeek,"</strong> issues refunds, credits, and transfers in respect
                  of tickets, sponsorships, and other paid entitlements purchased in respect of any LayerSeek event (each,
                  an <strong className="text-white">"Event"</strong>).
                </p>
                <p className="text-sm md:text-[15px] text-[var(--text-secondary)] leading-relaxed mt-3">
                  This Refund Policy is incorporated into, and forms part of, the LayerSeek Terms of Service, available at
                  layerseek.io/policy/terms (the <strong className="text-white">"Terms"</strong>), and capitalized terms
                  used but not defined herein shall have the meanings ascribed to them in the Terms. By purchasing a
                  ticket or sponsorship, you acknowledge that you have read, understood, and agree to be bound by this
                  Refund Policy.
                </p>
              </div>

              {/* Callout: All sales final */}
              <div
                className="mt-5 rounded-2xl border border-[var(--accent-primary)]/25 p-4 sm:p-5 flex items-start gap-3"
                style={{
                  background: 'linear-gradient(135deg, rgba(218,255,1,0.05) 0%, rgba(255,255,255,0.01) 100%)',
                }}
              >
                <div className="w-9 h-9 rounded-lg bg-[var(--accent-bg)] border border-[var(--accent-primary)]/40 flex items-center justify-center flex-shrink-0">
                  <FileWarning className="w-4 h-4 text-[var(--accent-primary)]" />
                </div>
                <div>
                  <div className="text-sm font-bold text-white mb-0.5">All sales are final</div>
                  <p className="text-xs sm:text-sm text-[var(--text-muted)] leading-relaxed">
                    Refunds are only issued if we cancel the Event outright and do not reschedule within 90 days. Everything
                    else, including missed sessions, change of mind, or unmet expectations, is non-refundable. Transfers
                    are permitted subject to the conditions in Section 6.
                  </p>
                </div>
              </div>
            </section>

            {/* 1. All Sales Final */}
            <SectionAnchor id="principle" num="1" title="General Principle: All Sales Final">
              <P num="1.1.">
                LayerSeek Events are virtual in nature, delivered on a scheduled date, offered with limited capacity, and
                subject to matchmaking allocations and other operational arrangements committed in advance of the Event
                date. Accordingly, all purchases of tickets, sponsorships, and any other paid entitlements are final and
                non-refundable, except as expressly provided in Section 2 (Event Cancellation) of this Refund Policy.
              </P>
            </SectionAnchor>

            {/* 2. Cancellation */}
            <SectionAnchor id="cancellation" num="2" title="Refund in the Event of Cancellation">
              <P num="2.1."><strong className="text-white">Full Refund on Cancellation.</strong> In the event that Zedit cancels an Event in its entirety and does not reschedule such Event to a new date within ninety (90) days of the originally scheduled date, each affected Participant shall be entitled to a full refund of the purchase price actually paid in respect of such Event.</P>
              <P num="2.2."><strong className="text-white">Timing of Refund.</strong> Refunds payable under Section 2.1 shall be processed within thirty (30) business days of Zedit’s public announcement of the cancellation.</P>
              <P num="2.3."><strong className="text-white">Method of Refund.</strong></P>
              <SubList
                items={[
                  { label: '(a)', text: 'Refunds in respect of purchases made by credit or debit card shall be issued to the original payment card used at the time of purchase.' },
                  { label: '(b)', text: 'Refunds in respect of purchases made in cryptocurrency (including but not limited to USDC) shall be issued in the same cryptocurrency to the wallet address from which payment was received, unless an alternative arrangement is agreed in writing between the Participant and Zedit.' },
                  { label: '(c)', text: 'Zedit shall not be liable for any exchange-rate movements, transaction fees, gas fees, or other costs incurred as a result of the refund transaction, save as required by applicable law.' },
                ]}
              />
            </SectionAnchor>

            {/* 3. Rescheduling */}
            <SectionAnchor id="rescheduling" num="3" title="Rescheduling">
              <P num="3.1."><strong className="text-white">Rescheduled Event.</strong> If Zedit reschedules an Event to a new date falling within one hundred and eighty (180) days of the originally scheduled date, all tickets and sponsorships shall remain valid for the rescheduled Event, and no refund shall be payable.</P>
              <P num="3.2."><strong className="text-white">Inability to Attend Rescheduled Date.</strong> Participants who are unable to attend the rescheduled date may transfer their ticket or sponsorship in accordance with Section 6 (Transfers).</P>
            </SectionAnchor>

            {/* 4. Non-Refundable */}
            <SectionAnchor id="non-refundable" num="4" title="Non-Refundable Circumstances">
              <P num="4.1.">For the avoidance of doubt, and without limiting the generality of Section 1, no refund, credit, or compensation of any kind shall be issued in respect of:</P>
              <SubList
                items={[
                  { label: '(a)', text: 'a change of mind, change of schedule, or inability to attend on the part of the Participant;' },
                  { label: '(b)', text: 'technical issues attributable to the Participant, including without limitation issues affecting the Participant’s internet connection, device, browser, wallet, or third-party account;' },
                  { label: '(c)', text: 'an inability to access the Event by reason of restrictions imposed on the Participant by their jurisdiction, employer, or any third party;' },
                  { label: '(d)', text: 'failure to achieve any specific commercial outcome, including without limitation the failure to receive or secure any grant, integration, partnership, investment, hire, acquisition, or contract, whether anticipated or otherwise;' },
                  { label: '(e)', text: 'the Event proceeding as scheduled where the Participant elects not to attend, whether announced in advance or not;' },
                  { label: '(f)', text: 'partial attendance, including without limitation missed sessions arising from time zone differences (in respect of which recordings shall be made available in accordance with the schedule communicated by Zedit);' },
                  { label: '(g)', text: 'changes to speakers, sponsors, session content, session order, or specific programming elements, provided that the Event proceeds substantially as advertised;' },
                  { label: '(h)', text: 'subjective dissatisfaction with the Virtual Venue, the Matchmaking System, match outcomes, or other aspects of the Event experience;' },
                  { label: '(i)', text: 'purchases made under a promotional code, referral code, discount, or comped arrangement, unless expressly stated otherwise at the time of purchase;' },
                  { label: '(j)', text: 'failure by the Participant to complete the Onboarding Form or to submit required Sponsor materials by the applicable deadlines; or' },
                  { label: '(k)', text: 'revocation of access pursuant to Section 3 of the Terms as a consequence of a breach of the Terms, the Code of Conduct, or the Prohibited Use provisions.' },
                ]}
              />
            </SectionAnchor>

            {/* 5. Sponsorships */}
            <SectionAnchor id="sponsorships" num="5" title="Sponsorships">
              <P num="5.1."><strong className="text-white">Non-Refundable.</strong> All Sponsor purchases, including Silver, Gold, Diamond, and any bespoke sponsorship arrangements, are final and non-refundable in all circumstances save for Event cancellation as defined in Section 2 above.</P>
              <P num="5.2."><strong className="text-white">Sponsor-Specific Non-Refundable Circumstances.</strong> Without limiting Section 4, no refund shall be issued to a Sponsor in respect of:</P>
              <SubList
                items={[
                  { label: '(a)', text: 'the Sponsor’s failure to submit required creative, technical, or personnel materials by the deadlines communicated by Zedit;' },
                  { label: '(b)', text: 'the non-attendance or non-use of allocated seats, booths, tables, or speaker slots by the Sponsor’s designated team members;' },
                  { label: '(c)', text: 'the Sponsor’s subjective assessment that the Event did not deliver anticipated commercial results;' },
                  { label: '(d)', text: 'the withdrawal, non-attendance, or removal of featured protocols under a Diamond Sponsor’s dedicated floor, whether by choice of such protocols or by Zedit’s exercise of its rights under Section 5.5 of the Terms; or' },
                  { label: '(e)', text: 'any other cause not expressly identified in Section 2.' },
                ]}
              />
              <P num="5.3."><strong className="text-white">Sponsor Responsibility.</strong> Each Sponsor is solely responsible for ensuring that its designated team members and any featured protocols under its sponsorship are prepared, informed, and available to participate on the Event date. Unused entitlements shall be forfeited upon conclusion of the Event and shall not carry forward.</P>
            </SectionAnchor>

            {/* 6. Transfers */}
            <SectionAnchor id="transfers" num="6" title="Transfers">
              <P num="6.1."><strong className="text-white">General.</strong> Solo Attendee tickets and Sponsor entitlements may only be transferred in accordance with this Section 6. Any purported transfer that does not comply with the requirements set out below shall be void and of no effect.</P>

              <SubHeading>Solo Attendee Ticket Transfers</SubHeading>

              <P num="6.2."><strong className="text-white">Eligibility for Transfer.</strong> A Solo Attendee ticket may be transferred to another individual, subject to the conditions set out in Sections 6.3 to 6.7 below. Transfers are permitted at no additional cost.</P>
              <P num="6.3."><strong className="text-white">Transfer Window.</strong> Transfer requests in respect of Solo Attendee tickets must be received by Zedit not less than seven (7) days prior to the scheduled Event date. Requests submitted less than seven (7) days prior to the Event date may be accommodated at Zedit’s sole discretion but are not guaranteed, as matchmaking allocations and Event operations may already be committed.</P>
              <P num="6.4."><strong className="text-white">Transfer Request Procedure.</strong> Transfer requests shall be submitted by electronic mail to <a href="mailto:hi@zedit.io" className="text-[var(--accent-primary)] hover:underline font-semibold">hi@zedit.io</a> and shall include:</P>
              <SubList
                items={[
                  { label: '(a)', text: 'the original order number or transaction reference;' },
                  { label: '(b)', text: 'the full name, electronic mail address, organization, role, and any other information reasonably required by Zedit in respect of the proposed transferee; and' },
                  { label: '(c)', text: 'written confirmation of the proposed transferee’s acceptance of the Terms and this Refund Policy.' },
                ]}
              />
              <P num="6.5."><strong className="text-white">Acceptance of the Proposed Transferee.</strong> No transfer shall become effective until Zedit has, in its sole discretion, accepted the proposed transferee. Zedit shall assess proposed transferees against the same standards applied to original Participants, including without limitation:</P>
              <SubList
                items={[
                  { label: '(a)', text: 'relevance to the target audience of the applicable Event, being, in the case of the LayerSeek DeFi Edition, professionals actively working in the decentralized finance ecosystem, including but not limited to protocol teams, founders, business development and partnerships leads, growth and marketing teams, ecosystem and grant teams of chains and foundations, infrastructure and tooling providers, and capital allocators;' },
                  { label: '(b)', text: 'satisfaction of the eligibility requirements set out in Section 2 of the Terms, including sanctions and legal capacity requirements;' },
                  { label: '(c)', text: 'the outcome of any reasonable verification undertaken by Zedit under Section 5 of the Terms; and' },
                  { label: '(d)', text: 'any other factor Zedit reasonably considers relevant to the integrity of the Event.' },
                ]}
              />
              <P num="6.6."><strong className="text-white">Refusal of Transfer.</strong> Zedit reserves the right to refuse any proposed transfer for any of the reasons set out in Section 6.5 above or on any other reasonable ground, including the grounds for refusal set out in Section 3 of the Terms. Refusal of a proposed transfer shall not entitle the original Participant to any refund, credit, or alternative remedy, and the original ticket shall remain valid and non-transferable to that specific transferee.</P>
              <P num="6.7."><strong className="text-white">Effect of Accepted Transfer.</strong> Upon Zedit’s written acceptance of a proposed transferee, the original ticket shall be reissued in the name of the transferee. The transferee shall be bound by the Terms and this Refund Policy in full, including all non-refund provisions. The original Participant shall thereafter have no further right, title, or interest in the transferred ticket.</P>

              <SubHeading>Sponsor Transfers</SubHeading>

              <P num="6.8."><strong className="text-white">General Principle.</strong> Sponsorships, including Silver, Gold, Diamond, and any custom sponsorship arrangements, are non-transferable except as expressly permitted under this Section 6. Sponsorships are inherently tied to the sponsoring organization’s brand, materials, team composition, floor or booth configuration, matchmaking positioning, and, in the case of Diamond Sponsors, featured protocol slate. Accordingly, Sponsor entitlements cannot be reassigned to an unrelated third-party organization in the manner permitted for Solo Attendee tickets.</P>
              <P num="6.9."><strong className="text-white">Permitted Sponsor Transfers.</strong> Zedit may, in its sole discretion, permit the following categories of transfer in respect of Sponsor entitlements:</P>
              <SubList
                items={[
                  { label: '(a)', text: 'Team Member Substitution. A Sponsor may substitute a designated team member with another individual from the same sponsoring organization, subject to the substitute completing the Onboarding Form and satisfying any applicable verification requirements under Section 5 of the Terms. Team member substitutions must be requested not less than three (3) days prior to the Event date.' },
                  { label: '(b)', text: 'Intra-Group Transfer. A Sponsor may request that the sponsorship be reassigned to a parent, subsidiary, or wholly-controlled affiliate of the original sponsoring organization, subject to Zedit’s acceptance and to the affiliate accepting the Terms and this Refund Policy. Intra-group transfer requests must be submitted not less than fourteen (14) days prior to the Event date.' },
                  { label: '(c)', text: 'Diamond Sponsor Featured Protocol Substitution. A Diamond Sponsor may substitute a featured protocol slot allocated under its dedicated floor with an alternative protocol, subject to the alternative protocol satisfying the same relevance and eligibility standards under Section 6.5 above, completing the Onboarding Form, and satisfying any applicable verification requirements. Featured protocol substitutions must be requested not less than fourteen (14) days prior to the Event date.' },
                ]}
              />
              <P num="6.10."><strong className="text-white">Prohibition on Third-Party Sponsor Assignment.</strong> For the avoidance of doubt, a Sponsor may not transfer, sell, or otherwise reassign its sponsorship to an unrelated third-party organization, whether at cost, at a mark-up, or gratuitously. Any purported transfer of a sponsorship to an unrelated third party shall be void and, at Zedit’s discretion, may result in forfeiture of the sponsorship without refund.</P>
              <P num="6.11."><strong className="text-white">Sponsor Transfer Procedure.</strong> Requests for permitted Sponsor transfers shall be submitted by electronic mail to <a href="mailto:hi@zedit.io" className="text-[var(--accent-primary)] hover:underline font-semibold">hi@zedit.io</a> and shall include the original order number, the details of the substitute team member, affiliate, or protocol (as applicable), and any supporting documentation Zedit reasonably requires to assess the request.</P>
              <P num="6.12."><strong className="text-white">Refusal of Sponsor Transfer.</strong> Zedit reserves the right to refuse any proposed Sponsor transfer, substitution, or reassignment on any reasonable ground. Refusal shall not entitle the Sponsor to any refund, credit, or alternative remedy.</P>
              <P num="6.13."><strong className="text-white">No Modification of Entitlements Through Transfer.</strong> No permitted transfer or substitution under this Section 6 shall entitle any transferee, substitute, affiliate, or featured protocol to any entitlement, benefit, or allocation greater than that of the original Participant or Sponsor. Transferees and substitutes step into the shoes of the original Participant or Sponsor for all purposes under the Terms and this Refund Policy.</P>
            </SectionAnchor>

            {/* 7. Chargebacks */}
            <SectionAnchor id="chargebacks" num="7" title="Chargebacks & Payment Disputes">
              <P num="7.1."><strong className="text-white">Notice Requirement.</strong> Before initiating any chargeback, payment dispute, or reversal with your card issuer, bank, or payment provider, you shall first contact Zedit at <a href="mailto:hi@zedit.io" className="text-[var(--accent-primary)] hover:underline font-semibold">hi@zedit.io</a> and allow a period of not less than ten (10) business days for Zedit to review and respond to your concern.</P>
              <P num="7.2."><strong className="text-white">Consequences of Unauthorized Chargebacks.</strong> Where a chargeback, dispute, or reversal is initiated without prior notice to Zedit as required by Section 7.1, or is initiated on grounds contrary to this Refund Policy, Zedit reserves the right, in its sole discretion, to:</P>
              <SubList
                items={[
                  { label: '(a)', text: 'revoke immediate access to the Event and any related services;' },
                  { label: '(b)', text: 'cancel any subsequent Event purchases made by the Participant, without refund;' },
                  { label: '(c)', text: 'permanently exclude the Participant from future Events; and' },
                  { label: '(d)', text: 'pursue recovery of the disputed amount, together with any associated fees, administrative costs, and legal expenses.' },
                ]}
              />
            </SectionAnchor>

            {/* 8. Force Majeure */}
            <SectionAnchor id="force-majeure" num="8" title="Force Majeure">
              <P num="8.1."><strong className="text-white">Definition.</strong> For the purposes of this Refund Policy, <strong className="text-white">"Force Majeure Event"</strong> means any event or circumstance beyond the reasonable control of Zedit, including without limitation: widespread failure of internet or telecommunications infrastructure; sustained outage or failure of critical third-party service providers on which the Event depends (including but not limited to the Virtual Venue provider, ticketing platform, and payment processors); acts of war, terrorism, insurrection, or civil unrest; acts of any governmental or regulatory authority; imposition of sanctions or trade restrictions; pandemic, epidemic, or public-health emergency; natural disaster, fire, flood, or extreme weather; or any similar event.</P>
              <P num="8.2."><strong className="text-white">Effect on Event.</strong> In the event that Zedit is prevented, hindered, or materially delayed from delivering an Event by reason of a Force Majeure Event, Zedit shall use commercially reasonable efforts to reschedule the Event to a new date. Rescheduling due to a Force Majeure Event shall be treated in accordance with Section 3 (Rescheduling) and shall not, in itself, entitle any Participant to a refund, provided that the Event is delivered within a reasonable timeframe having regard to the nature and duration of the Force Majeure Event.</P>
            </SectionAnchor>

            {/* 9. Statutory Rights */}
            <SectionAnchor id="statutory" num="9" title="Statutory Rights">
              <P num="9.1.">Nothing in this Refund Policy shall exclude, restrict, or modify any right of refund, cancellation, or withdrawal that you may have under any applicable mandatory consumer protection law that cannot be waived by contract.</P>
              <P num="9.2."><strong className="text-white">B2B Context.</strong> Participants are advised that the majority of LayerSeek purchases are made in a business-to-business capacity on behalf of a protocol, foundation, company, or other organization. Purchases made in such capacity are typically not covered by consumer-facing statutory rights in most jurisdictions. Participants purchasing in a consumer capacity from jurisdictions conferring non-waivable statutory rights (including certain jurisdictions within the European Union and United Kingdom) retain such rights notwithstanding this Refund Policy.</P>
            </SectionAnchor>

            {/* 10. Amendments */}
            <SectionAnchor id="amendments" num="10" title="Amendments">
              <P num="10.1.">Zedit reserves the right to amend this Refund Policy from time to time. The version of this Refund Policy in effect as of the date of your purchase shall govern that purchase. The current version of this Refund Policy shall at all times be available at layerseek.io/policy/refunds.</P>
            </SectionAnchor>

            {/* 11. Contact */}
            <SectionAnchor id="contact" num="11" title="Contact">
              <P>For any queries regarding this Refund Policy or to submit a transfer request, please contact:</P>
              <div
                className="mt-2 p-5 sm:p-6 rounded-2xl border border-[var(--accent-primary)]/25"
                style={{
                  background: 'linear-gradient(135deg, rgba(218,255,1,0.05) 0%, rgba(255,255,255,0.01) 100%)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                }}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-[var(--accent-bg)] border border-[var(--accent-primary)]/40 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-[var(--accent-primary)]" />
                  </div>
                  <div>
                    <div className="text-white font-semibold">Zedit Limited</div>
                    <div className="text-xs text-[var(--text-muted)]">t/a LayerSeek</div>
                  </div>
                </div>
                <div className="flex items-start gap-3 mb-3">
                  <MapPin className="w-4 h-4 text-[var(--accent-primary)] mt-1 flex-shrink-0" />
                  <div className="text-sm text-[var(--text-secondary)] leading-relaxed">
                    Office A, RAK DAO Business Centre, ROC Office<br />
                    Ground Floor, Al Rifaa, Sheikh Mohammed Bin Zayed Road<br />
                    Ras Al Khaimah, United Arab Emirates
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-[var(--accent-primary)] flex-shrink-0" />
                  <a
                    href="mailto:hi@zedit.io"
                    data-testid="refund-contact-email"
                    className="text-sm font-semibold text-white hover:text-[var(--accent-primary)] transition-colors"
                  >
                    hi@zedit.io
                  </a>
                </div>
              </div>
            </SectionAnchor>
          </div>
        </div>
      </div>

      {/* Back to top */}
      {showTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          data-testid="refund-back-to-top"
          aria-label="Back to top"
          className="fixed bottom-6 right-6 z-40 w-11 h-11 rounded-full bg-[var(--accent-primary)] text-[var(--bg-primary)] flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}

      <Footer />
    </div>
  );
};

export default LayerSeekRefundPolicyPage;
